import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Typography, Stack, Button, Box, InputAdornment, IconButton } from '@mui/material';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

import { AuthErrorMessage, CodeExpiryNotice } from 'components/auth';
import { Input } from 'components/UI/Input';
import { PasswordStrengthMeter } from 'components/UI/PasswordStrengthMeter';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@repo/shadcn-ui';

import { useSendMagicAuthCode, useVerifyMagicAuthCode, useSetupPassword } from 'hooks/api/useWorkOSAuth';
import { useAuth } from 'hooks';
import { OTP_CODE_TTL_SECONDS, useCodeExpiry } from 'hooks/useCodeExpiry';
import { CompanyContext } from 'contexts/companyContext';
import { getEmbeddedKey, otpVerifyErrorMessage } from 'utils';
import { markPendingPostAuthStep, clearPendingPostAuthStep } from 'utils/postAuthStep';
import { emailRex } from 'settings';
import { ROUTES } from 'routes';

type Step = 'request' | 'code' | 'setPassword' | 'done';

/**
 * VERA-837: Forgot password lives on its own page (previously handled inline
 * inside SignInBanner, which felt like nothing happened on click). Reuses the
 * existing owner-OTP plumbing: send code → verify code → set a new password.
 */
export const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState((location.state as { email?: string } | null)?.email ?? '');
  const [code, setCode] = useState('');
  // VRG-720 — send time of the outstanding code, for its visible deadline.
  const [codeIssuedAt, setCodeIssuedAt] = useState<number | null>(null);
  const { expired: codeExpired } = useCodeExpiry(codeIssuedAt, OTP_CODE_TTL_SECONDS);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isSendingCode, setIsSendingCode] = useState(false);
  const sendCodeInFlightRef = useRef(false);

  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const verifyInFlightRef = useRef(false);

  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const setPasswordInFlightRef = useRef(false);

  const continueInFlightRef = useRef(false);

  // Mirrors SignInBanner's suppressAutoNavRef: keeps the isAuthenticated
  // auto-navigate effect from firing while we're showing the post-verify
  // "set a password" step.
  const suppressAutoNavRef = useRef(false);

  const sendMagicAuthCode = useSendMagicAuthCode();
  const verifyMagicAuthCode = useVerifyMagicAuthCode();
  const setupPassword = useSetupPassword();

  const { companyId } = useContext(CompanyContext);

  useEffect(() => {
    if (isAuthenticated && !suppressAutoNavRef.current) {
      navigate(ROUTES.CERTIFICATES);
    }
  }, [isAuthenticated]);

  const handleSendCode = async () => {
    if (sendCodeInFlightRef.current) return;

    if (!email || !emailRex.test(email)) {
      setError('Enter a valid email');
      return;
    }

    sendCodeInFlightRef.current = true;
    setIsSendingCode(true);
    setError(null);
    try {
      await sendMagicAuthCode.mutateAsync({ email, companyId });
      setStep('code');
      // Resend overwrites the stored code and restarts its TTL.
      setCodeIssuedAt(Date.now());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to send the code. Please try again.');
    } finally {
      sendCodeInFlightRef.current = false;
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verifyInFlightRef.current) return;

    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code.');
      return;
    }

    verifyInFlightRef.current = true;
    setIsVerifyingCode(true);
    setError(null);
    suppressAutoNavRef.current = true;
    // VERA-837: must be set BEFORE the mutation — see SignInBanner's
    // handleVerifyCode for the full explanation (onSuccess flips
    // isAuthenticated mid-await, which would otherwise let ProtectedAuthRoute
    // redirect away before the set-password step renders).
    markPendingPostAuthStep();

    try {
      const embeddKey = getEmbeddedKey();

      await verifyMagicAuthCode.mutateAsync({
        email,
        code,
        embeddKey: embeddKey || undefined,
        companyId,
      });

      setStep('setPassword');
    } catch (err: any) {
      clearPendingPostAuthStep();
      suppressAutoNavRef.current = false;
      // Only claims the code is bad when the SERVER said so. Both branches of the
      // ternary this replaces ended at the invalid-code message, so a server-side
      // failure after a SUCCESSFUL code check was reported as a bad code — which is
      // exactly what happened when owner provisioning was throwing.
      setError(otpVerifyErrorMessage(err));
      setCode('');
    } finally {
      verifyInFlightRef.current = false;
      setIsVerifyingCode(false);
    }
  };

  const handleSetPassword = async () => {
    if (setPasswordInFlightRef.current) return;

    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setPasswordInFlightRef.current = true;
    setIsSettingPassword(true);
    setError(null);
    try {
      await setupPassword.mutateAsync(newPassword);
      // VERA-837: show the on-page confirmation instead of navigating away
      // immediately. The pending-post-auth-step flag (set in handleVerifyCode)
      // MUST remain set here — ProtectedAuthRoute otherwise redirects the now-
      // authenticated user away from this page before the 'done' step renders.
      // It's cleared by handleContinueFromDone, along with suppressAutoNavRef.
      setStep('done');
    } catch (err: any) {
      // VERA-837: the backend now returns 400 with WorkOS's real validation
      // message (e.g. "Password does not meet strength requirements.")
      // instead of a generic 500 — show it verbatim, with an actionable hint
      // when it's the strength-policy case. Only fall back to the generic
      // message for genuine non-validation errors (no message at all).
      const backendMsg: string | undefined = err?.response?.data?.message;
      if (backendMsg) {
        const isStrengthError = /strength requirement/i.test(backendMsg);
        setError(isStrengthError ? `${backendMsg} Try a longer, less common phrase.` : backendMsg);
      } else {
        setError('Failed to set password. You can try again later from your profile.');
      }
    } finally {
      setPasswordInFlightRef.current = false;
      setIsSettingPassword(false);
    }
  };

  const handleSkipSetPassword = () => {
    clearPendingPostAuthStep();
    suppressAutoNavRef.current = false;
    navigate('/certificates');
  };

  const handleContinueFromDone = () => {
    if (continueInFlightRef.current) return;
    continueInFlightRef.current = true;
    clearPendingPostAuthStep();
    suppressAutoNavRef.current = false;
    navigate('/certificates');
  };

  const handleReturnToLogin = () => {
    clearPendingPostAuthStep();
    suppressAutoNavRef.current = false;
    navigate(ROUTES.SIGN_IN);
  };

  // VERA-842: the title mirrors the "Forgot password?" link that got the user
  // here (destination matches its entry point), while the button states the
  // action taken ("Reset password").
  const titles: Record<Step, string> = {
    request: 'Forgot password',
    code: 'Forgot password',
    setPassword: 'Set a password',
    done: 'Password updated',
  };

  return (
    <div className='flex flex-col items-center pt-[20vh] pb-8 px-4 bg-background min-h-screen w-full'>
      <div className='w-full max-w-[400px]'>
        <h2 className='mb-4 text-[24px] leading-[32px] font-[400]'>{titles[step]}</h2>
        <Stack sx={{ width: '100%', maxWidth: '400px' }} gap={2}>
          {step === 'request' && (
            <Box>
              <Typography component='p' variant='body3' color='text.secondary' sx={{ mb: 2, lineHeight: 1.5 }}>
                Enter your email and we'll send you a code to reset your password.
              </Typography>
              <Input
                type='email'
                value={email}
                onChange={setEmail}
                placeholder='Email'
                sx={{ mb: 2, '& input': { paddingLeft: '8px' } }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (!sendCodeInFlightRef.current) handleSendCode();
                  }
                }}
              />
              <AuthErrorMessage message={error} />
              <Stack spacing={2}>
                <Button
                  variant='contained'
                  fullWidth
                  onClick={handleSendCode}
                  // VERA-837: never disabled — clicking with a missing/invalid
                  // email surfaces the inline validation message instead of a
                  // dead button. Double-submit is guarded by sendCodeInFlightRef.
                  sx={{ paddingX: 2 }}
                >
                  {isSendingCode ? 'Sending...' : 'Reset password'}
                </Button>
                <Button variant='text' fullWidth onClick={handleReturnToLogin} sx={{ paddingX: 2 }}>
                  Return to login
                </Button>
              </Stack>
            </Box>
          )}

          {step === 'code' && (
            <Box>
              {/*
                VERA-837: match the other steps' description styling exactly
                (body3 / text.secondary / lineHeight 1.5) — this one was body2
                and full-strength text, so it read differently. mb:4 not 2
                because a LABELLED group follows here; the other steps' 8px
                sits against a bare input, so 8px here collapsed the
                description into the "Verification Code" label.
              */}
              <Typography component='p' variant='body3' color='text.secondary' sx={{ mb: 4, lineHeight: 1.5 }}>
                Enter the 6-digit code sent to {email}
              </Typography>
              <div className='mb-4'>
                <div className='flex flex-col'>
                  <Typography className='mb-2'>Verification Code</Typography>
                  <InputOTP
                    onChange={(value: string) => {
                      const cleanedValue = value.replace(/\D/g, '').slice(0, 6);
                      setCode(cleanedValue);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!verifyInFlightRef.current) handleVerifyCode();
                      }
                    }}
                    value={code}
                    maxLength={6}
                    id='otp'
                    required
                    containerClassName='w-full'
                  >
                    <InputOTPGroup className='flex w-full gap-2 *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border'>
                      <InputOTPSlot className='rounded-md border flex-1 h-12' index={0} />
                      <InputOTPSlot className='rounded-md border flex-1 h-12' index={1} />
                      <InputOTPSlot className='rounded-md border flex-1 h-12' index={2} />
                      <InputOTPSlot className='rounded-md border flex-1 h-12' index={3} />
                      <InputOTPSlot className='rounded-md border flex-1 h-12' index={4} />
                      <InputOTPSlot className='rounded-md border flex-1 h-12' index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              <CodeExpiryNotice
                issuedAt={codeIssuedAt}
                ttlSeconds={OTP_CODE_TTL_SECONDS}
                resendLabel='Use Resend code below'
              />
              <AuthErrorMessage message={error} />
              <Stack spacing={2}>
                {/*
                  VRG-720: Resend takes the fill once the code has expired;
                  Verify stays clickable, since the countdown predicts the
                  server's answer rather than being it. Fuller note in
                  OnboardingAuthPanel.
                */}
                <Button variant={codeExpired ? 'outlined' : 'contained'} fullWidth onClick={handleVerifyCode} sx={{ paddingX: 2 }}>
                  {isVerifyingCode ? 'Verifying...' : 'Verify code'}
                </Button>
                <Button
                  variant={codeExpired ? 'contained' : 'outlined'}
                  fullWidth
                  onClick={handleSendCode}
                  disabled={isSendingCode || isVerifyingCode}
                  sx={{ paddingX: 2 }}
                >
                  {isSendingCode ? 'Sending...' : 'Resend code'}
                </Button>
                <Button
                  variant='text'
                  fullWidth
                  onClick={handleReturnToLogin}
                  disabled={isVerifyingCode}
                  sx={{ paddingX: 2 }}
                >
                  Return to login
                </Button>
              </Stack>
            </Box>
          )}

          {step === 'setPassword' && (
            <Box>
              <Typography component='p' variant='body3' color='text.secondary' sx={{ mb: 2, lineHeight: 1.5 }}>
                Choose a new password to use next time.
              </Typography>
              <Input
                type={showNewPassword ? 'text' : 'password'}
                placeholder='New password'
                value={newPassword}
                // VERA-837: clear a stale submit error as soon as the user
                // edits the password. Without this, a failed attempt's
                // AuthErrorMessage stays on screen while they keep typing,
                // so every subsequent attempt looks like it also failed.
                onChange={value => {
                  setNewPassword(value)
                  if (error) setError(null)
                }}
                // VERA-837: mb:2 for parity with the request step's email
                // field, which already sets it. Without it the button sits
                // flush against the input on this step only.
                sx={{ mb: 2, '& input': { paddingLeft: '8px' } }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (!setPasswordInFlightRef.current) handleSetPassword();
                  }
                }}
                endAdornment={
                  <InputAdornment position='end' sx={{ pr: 1 }}>
                    <IconButton
                      aria-label={showNewPassword ? 'hide the password' : 'display the password'}
                      onClick={() => setShowNewPassword(s => !s)}
                      edge='end'
                    >
                      {showNewPassword ? (
                        <EyeOffIcon className='w-5 h-5' strokeWidth={1} />
                      ) : (
                        <EyeIcon className='w-5 h-5' strokeWidth={1} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {/*
                VERA-837: password policy is zxcvbn-style strength scoring
                (not a rules checklist) — show live client-side feedback so
                the user isn't guessing, mirroring WorkOS's server-side check.
              */}
              <PasswordStrengthMeter password={newPassword} />
              <AuthErrorMessage message={error} />
              <Stack spacing={2}>
                <Button variant='contained' fullWidth onClick={handleSetPassword} sx={{ paddingX: 2 }}>
                  {isSettingPassword ? 'Setting password...' : 'Set password'}
                </Button>
                <Button
                  variant='text'
                  fullWidth
                  onClick={handleSkipSetPassword}
                  disabled={isSettingPassword}
                  sx={{ paddingX: 2 }}
                >
                  Skip for now
                </Button>
              </Stack>
            </Box>
          )}

          {step === 'done' && (
            <Box>
              <Typography component='p' variant='body3' color='text.secondary' sx={{ mb: 2, lineHeight: 1.5 }}>
                Your password was updated. We sent a confirmation email to {email}.
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant='contained'
                  fullWidth
                  onClick={handleContinueFromDone}
                  // VERA-837: never disabled — this is a simple navigation
                  // action, not a validated submit. Double-click is guarded
                  // by continueInFlightRef.
                  sx={{ paddingX: 2 }}
                >
                  Continue
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </div>
    </div>
  );
};

export default ForgotPassword;
